/**
 * Custom hooks for date range state management
 * 
 * These hooks provide convenient access to date range state and actions,
 * including persistence and URL synchronization.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useEquipmentContext } from '../contexts/equipment-context';
import { DateRange } from '../services/equipment/types';
import {
  getDefaultDateRange,
  isValidDateRange
} from '../components/equipment-status/date-range-selector/date-range-utils';

/**
 * Hook for date range state management
 */
export const useDateRange = () => {
  const { state, dispatch } = useEquipmentContext();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced set date range
  const setDateRange = useCallback((dateRange: DateRange) => {
    // Validate the date range before setting
    if (!isValidDateRange(dateRange.startDate, dateRange.endDate)) {
      console.warn('Invalid date range provided:', dateRange);
      return;
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new debounced timeout
    timeoutRef.current = setTimeout(() => {
      dispatch({ type: 'SET_DATE_RANGE', payload: dateRange });
      timeoutRef.current = null;
    }, 300); // 300ms debounce
  }, [dispatch]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Reset to default date range
  const resetDateRange = useCallback(() => {
    const defaultRange = getDefaultDateRange();
    setDateRange(defaultRange);
  }, [setDateRange]);

  return {
    dateRange: state.dateRange,
    setDateRange,
    resetDateRange
  };
};

/**
 * Hook for date range persistence in URL parameters
 */
export const useDateRangePersistence = () => {
  const { dateRange, setDateRange } = useDateRange();

  // Load date range from URL parameters on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const startDate = urlParams.get('startDate');
    const endDate = urlParams.get('endDate');

    if (startDate && endDate && isValidDateRange(startDate, endDate)) {
      setDateRange({ startDate, endDate });
    }
  }, [setDateRange]);

  // Update URL parameters when date range changes
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('startDate', dateRange.startDate);
    url.searchParams.set('endDate', dateRange.endDate);

    // Update URL without triggering a page reload
    window.history.replaceState(null, '', url.toString());
  }, [dateRange]);

  return {
    dateRange
  };
};

/**
 * Hook for date range persistence in localStorage
 */
export const useDateRangeLocalStorage = (key: string = 'equipmentDateRange') => {
  const { dateRange, setDateRange } = useDateRange();

  // Load date range from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsedRange: DateRange = JSON.parse(stored);
        if (isValidDateRange(parsedRange.startDate, parsedRange.endDate)) {
          setDateRange(parsedRange);
        }
      }
    } catch (error) {
      console.warn('Failed to load date range from localStorage:', error);
    }
  }, [key, setDateRange]);

  // Save date range to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(dateRange));
    } catch (error) {
      console.warn('Failed to save date range to localStorage:', error);
    }
  }, [dateRange, key]);

  return {
    dateRange
  };
};

/**
 * Hook that combines date range management with persistence
 * Uses URL parameters by default, with localStorage as fallback
 */
export const useDateRangeWithPersistence = (
  persistenceType: 'url' | 'localStorage' | 'both' = 'url'
) => {
  const dateRangeHook = useDateRange();

  // Apply persistence based on type
  if (persistenceType === 'url' || persistenceType === 'both') {
    useDateRangePersistence();
  }

  if (persistenceType === 'localStorage' || persistenceType === 'both') {
    useDateRangeLocalStorage();
  }

  return dateRangeHook;
};

/**
 * Hook for date range validation and utilities
 */
export const useDateRangeValidation = () => {
  const { dateRange } = useDateRange();

  // Check if current date range is valid
  const isCurrentRangeValid = useCallback(() => {
    return isValidDateRange(dateRange.startDate, dateRange.endDate);
  }, [dateRange]);

  // Get the duration of the current date range in days
  const getRangeDurationInDays = useCallback(() => {
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }, [dateRange]);

  // Check if the date range is within a maximum duration
  const isWithinMaxDuration = useCallback((maxDays: number) => {
    return getRangeDurationInDays() <= maxDays;
  }, [getRangeDurationInDays]);

  // Check if a specific date is within the current range
  const isDateInRange = useCallback((date: string) => {
    return date >= dateRange.startDate && date <= dateRange.endDate;
  }, [dateRange]);

  return {
    isCurrentRangeValid,
    getRangeDurationInDays,
    isWithinMaxDuration,
    isDateInRange
  };
};